import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import {
  UserCheck, Car, Plane, DoorOpen, ShieldCheck, HeartHandshake, Info, PlaneTakeoff,
  MapPin, Building2, Clock, FileText, Home, ArrowRight, Phone, MessageSquare,
} from "lucide-react";
import { AIRPORT_RATES, LOCAL_RATES } from "@/data/rates";
import api from "@/lib/api";
import { useSEO } from "@/lib/seo";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/1bb89d72-67bd-439a-b04d-415fc0b99be3/images/3b4f394b8531fa8c0f56b3161f016252431fc26a2c817d0c53bb3fd22795adec.jpeg";
const PHONE = "+19703156500";
const CANONICAL = "https://www.aspenexecutivelimos.com/rates";

const REGIONAL_TOS = ["Willits, El Jebel", "Carbondale", "Glenwood Springs", "Crested Butte", "Vail, Beaver Creek"];

const CTA_BTN = "inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-bold text-base tracking-wide rounded-sm px-[3px] py-[1px] shadow-xl hover:bg-[#E0BC4A] transition-colors";

const INCLUDED = [
  { icon: UserCheck, title: "Professional Local Chauffeur", desc: "Knowledgeable, courteous drivers who know every route." },
  { icon: Car, title: "Luxury 4WD SUV", desc: "Immaculate four-wheel-drive SUVs built for the mountains." },
  { icon: Plane, title: "Flight Monitoring", desc: "We track your flight and adjust for airport pickups." },
  { icon: DoorOpen, title: "Door-to-Door Service", desc: "Private curbside pickup and drop-off, every time." },
  { icon: ShieldCheck, title: "All-Season Reliability", desc: "Safe, dependable transportation in every condition." },
  { icon: HeartHandshake, title: "Personalized Service", desc: "Attentive care built around your schedule." },
];

const RELATED = [
  { icon: Plane, title: "Airport Transportation", desc: "Chauffeured transfers to and from every regional airport.", to: "/airport-transportation", testid: "related-airport" },
  { icon: MapPin, title: "Aspen Area Transportation", desc: "Dining, shopping, events, and local destinations.", to: "/aspen-area-transportation", testid: "related-aspen" },
  { icon: Car, title: "Fleet", desc: "Luxury four-wheel-drive SUVs built for Colorado travel.", to: "/fleet", testid: "related-fleet" },
  { icon: Building2, title: "Corporate Transportation", desc: "Punctual, discreet service for executives and clients.", to: "/#services", testid: "related-corporate" },
  { icon: Clock, title: "Hourly Hire", desc: "A chauffeur at your disposal by the hour or the day.", to: "/#services", testid: "related-hourly" },
  { icon: FileText, title: "Request a Quote", desc: "Receive a personalized quote for your itinerary.", to: "/reserve", testid: "related-quote" },
  { icon: Home, title: "Home", desc: "Return to the Aspen Executive Limo homepage.", to: "/", testid: "related-home" },
];

const FAQS = [
  { q: "Are your rates flat rates?", a: "Yes. Our published rates are flat, one-way rates for our most frequently requested routes — simple and transparent, with no hidden surprises." },
  { q: "Do rates change during severe weather?", a: "Under normal driving conditions, our published rates apply. During severe weather, road closures, chain restrictions, or extraordinary travel conditions, pricing may be adjusted to reflect additional travel time and operating conditions. Any adjustment is always discussed before your reservation is confirmed." },
  { q: "Are airport pickup fees included?", a: "Airport transfers include flight monitoring and door-to-door curbside service. For private aviation, FBO facility fees charged by the airport or FBO are separate from our transportation charges." },
  { q: "What if my destination isn't listed?", a: "We provide transportation throughout Aspen, Snowmass, Basalt, Carbondale, Glenwood Springs, Denver, Eagle, Rifle, Grand Junction, and destinations across Colorado. Just contact us for a personalized quote." },
  { q: "How do I request a custom quote?", a: "Use our Request a Quote form with your trip details, or call or text us directly — we'll provide personalized pricing and availability." },
];

const SEO_DESC = "Transparent flat-rate pricing from Aspen Executive Limo for our most popular Aspen airport and regional destinations. Luxury private car service with straightforward rates and custom quotes for any itinerary.";

const BUSINESS_LD = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://www.aspenexecutivelimos.com/#business",
  name: "Aspen Executive Limo",
  description: SEO_DESC,
  url: CANONICAL,
  image: HERO_IMG,
  telephone: "+1-970-315-6500",
  priceRange: "$$$",
  address: { "@type": "PostalAddress", addressLocality: "Aspen", addressRegion: "CO", addressCountry: "US" },
  areaServed: [
    { "@type": "City", name: "Aspen, CO" },
    { "@type": "City", name: "Snowmass, CO" },
    { "@type": "City", name: "Denver, CO" },
    { "@type": "City", name: "Glenwood Springs, CO" },
  ],
};

const BREADCRUMB_LD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://www.aspenexecutivelimos.com/" },
    { "@type": "ListItem", position: 2, name: "Rates", item: CANONICAL },
  ],
};

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

function RatesTable({ title, rates, testid }) {
  if (!rates.length) return null;
  return (
    <div data-testid={testid}>
      <h3 className="font-heading text-2xl sm:text-3xl font-light tracking-tight text-white mb-5">{title}</h3>
      <div className="rounded-sm border border-white/10 overflow-hidden bg-[#101012]">
        <table className="w-full">
          <thead>
            <tr className="bg-[#D4AF37]/10 border-b border-[#D4AF37]/20">
              <th className="text-left px-5 py-3 text-xs tracking-label uppercase gold-text font-medium">From</th>
              <th className="text-left px-5 py-3 text-xs tracking-label uppercase gold-text font-medium">To</th>
              <th className="text-right px-5 py-3 text-xs tracking-label uppercase gold-text font-medium">Flat Rate</th>
            </tr>
          </thead>
          <tbody>
            {rates.map((r, i) => (
              <tr key={i} data-testid={`${testid}-row-${i}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors">
                <td className="px-5 py-3.5 text-white font-light text-sm sm:text-base align-middle">{r.from}</td>
                <td className="px-5 py-3.5 text-zinc-300 font-light text-sm sm:text-base align-middle">{r.to}</td>
                <td className="px-5 py-3.5 text-right gold-text font-heading text-xl sm:text-2xl whitespace-nowrap align-middle">${r.rate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Rates() {
  const [airportRates, setAirportRates] = useState(AIRPORT_RATES);
  const [localRates, setLocalRates] = useState(LOCAL_RATES);

  useSEO({
    title: "Aspen Limo Rates | Transparent Flat-Rate Pricing — Aspen Executive Limo",
    description: SEO_DESC,
    keywords: "Aspen limo rates, Aspen transportation rates, Denver to Aspen transportation cost, Aspen airport transportation pricing, luxury transportation Aspen, private car service Aspen",
    canonical: CANONICAL,
    image: HERO_IMG,
    jsonLd: [BUSINESS_LD, BREADCRUMB_LD, FAQ_LD],
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    api.get("/rates").then((res) => {
      if (res.data?.airport?.length) setAirportRates(res.data.airport);
      if (res.data?.local?.length) setLocalRates(res.data.local);
    }).catch(() => {});
  }, []);

  const regional = localRates.filter((r) => REGIONAL_TOS.includes(r.to));
  const mountain = localRates.filter((r) => !REGIONAL_TOS.includes(r.to));

  return (
    <div className="min-h-screen" data-testid="rates-page">
      <Navbar />

      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 z-0" aria-hidden="true">
          <img src={HERO_IMG} alt="Luxury SUV in an Aspen mountain setting" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-[120px] pb-24 sm:pt-[160px] sm:pb-32">
          <p className="text-xs tracking-label uppercase font-bold text-[#D4AF37] mb-4" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.9)" }}>Aspen Executive Limo</p>
          <h1 className="font-heading text-5xl sm:text-7xl font-light leading-[1.05] tracking-tight text-white max-w-3xl" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.9)" }}>
            Transparent Flat-Rate Pricing
          </h1>
          <p className="mt-6 max-w-2xl text-lg sm:text-xl text-zinc-100 font-light leading-relaxed" style={{ textShadow: "0 1px 6px rgba(0,0,0,0.85)" }}>
            Luxury transportation with straightforward pricing for our most frequently requested destinations. If you don't see your destination listed, we're happy to provide a personalized quote.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/reserve" data-testid="rates-hero-quote-btn" className={CTA_BTN}>Request a Quote <ArrowRight size={16} /></Link>
            <a href={`sms:${PHONE}`} data-testid="rates-hero-calltext-btn" className={CTA_BTN}><MessageSquare size={16} /> Call or Text Now</a>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white">Simple, Honest Pricing</h2>
        <div className="mt-4 h-px w-16 bg-[#D4AF37]/60 mx-auto" />
        <p className="mt-6 text-lg text-zinc-300 font-light leading-relaxed">
          The rates below represent our most common transportation routes, offered as clear flat rates so you always know what to expect — no hidden surprises. For any destination not listed, we're glad to prepare a personalized quote tailored to your itinerary.
        </p>
      </section>

      {/* POPULAR ROUTES & RATES */}
      <section className="bg-black/40 backdrop-blur-md border-y border-white/10 py-20" data-testid="rates-tables">
        <div className="max-w-4xl mx-auto px-6 space-y-14">
          <RatesTable title="Airport Transportation" rates={airportRates} testid="rates-airport-table" />
          <RatesTable title="Regional Destinations" rates={regional} testid="rates-regional-table" />
          <RatesTable title="Popular Mountain Routes" rates={mountain} testid="rates-mountain-table" />
          <p className="text-center text-zinc-500 font-light text-sm">Rates are one-way and subject to availability.</p>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white text-center">Every Reservation Includes</h2>
        <div className="mt-4 h-px w-16 bg-[#D4AF37]/60 mx-auto" />
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {INCLUDED.map((f) => (
            <div key={f.title} data-testid={`included-${f.title}`} className="group bg-black/40 backdrop-blur-md border border-white/10 rounded-sm p-8 hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all duration-500">
              <f.icon size={26} strokeWidth={1.5} className="gold-text mb-5" />
              <h3 className="font-heading text-xl mb-2 text-white">{f.title}</h3>
              <p className="text-zinc-400 font-light text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* IMPORTANT INFORMATION */}
      <section className="bg-black/40 backdrop-blur-md border-y border-white/10 py-16" data-testid="rates-important-info">
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Info size={22} className="gold-text" />
            <h2 className="font-heading text-2xl sm:text-3xl font-light tracking-tight text-white">Important Information</h2>
          </div>
          <p className="text-zinc-300 font-light leading-relaxed text-center">
            Published rates apply under normal driving conditions. During periods of severe weather, road closures, chain restrictions, or extraordinary travel conditions, pricing may be adjusted to reflect additional travel time and operating conditions. Any adjustments will always be discussed before your reservation is confirmed.
          </p>
          <p className="mt-5 text-zinc-300 font-light leading-relaxed text-center">
            Private jet arrivals and departures may be subject to applicable FBO facility fees charged by the airport or FBO. These fees are separate from Aspen Executive Limo's transportation charges.
          </p>
        </div>
      </section>

      {/* NEED A DIFFERENT DESTINATION */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center" data-testid="rates-custom-destination">
        <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white">Don't See Your Destination?</h2>
        <div className="mt-4 h-px w-16 bg-[#D4AF37]/60 mx-auto" />
        <p className="mt-6 text-lg text-zinc-300 font-light leading-relaxed">
          Aspen Executive Limo provides transportation throughout Aspen, Snowmass, Basalt, Carbondale, Glenwood Springs, Denver, Eagle, Rifle, Grand Junction, and destinations across Colorado. Contact us for a personalized quote tailored to your itinerary.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/reserve" data-testid="rates-custom-quote-btn" className={CTA_BTN}>Request a Quote <ArrowRight size={16} /></Link>
          <a href={`tel:${PHONE}`} data-testid="rates-custom-call-btn" className={CTA_BTN}><Phone size={16} /> Call or Text Now</a>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-black/40 backdrop-blur-md border-y border-white/10 py-20" data-testid="rates-faq">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white text-center">Frequently Asked Questions</h2>
          <div className="mt-4 h-px w-16 bg-[#D4AF37]/60 mx-auto" />
          <div className="mt-10 space-y-4">
            {FAQS.map((f, i) => (
              <div key={i} data-testid={`rates-faq-item-${i}`} className="bg-[#101012] border border-white/10 rounded-sm p-6 hover:border-[#D4AF37]/40 transition-colors">
                <h3 className="font-heading text-xl text-white">{f.q}</h3>
                <p className="mt-3 text-zinc-400 font-light text-sm leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTINUE PLANNING YOUR TRIP */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="rates-related">
        <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white text-center">Continue Planning Your Trip</h2>
        <div className="mt-4 h-px w-16 bg-[#D4AF37]/60 mx-auto" />
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {RELATED.map((r) => (
            <Link key={r.title} to={r.to} data-testid={r.testid} className="group bg-black/40 backdrop-blur-md border border-white/10 rounded-sm p-8 hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all duration-500">
              <r.icon size={26} strokeWidth={1.5} className="gold-text mb-5" />
              <h3 className="font-heading text-xl mb-2 text-white flex items-center gap-2">
                {r.title}
                <ArrowRight size={18} className="gold-text opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </h3>
              <p className="text-zinc-400 font-light text-sm leading-relaxed">{r.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-black/40 backdrop-blur-md border-t border-white/10 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white">Ready to Reserve Your Ride?</h2>
          <p className="mt-4 max-w-2xl mx-auto text-zinc-300 font-light leading-relaxed">
            Whether you're traveling to the airport, a resort, a private residence, or anywhere in Colorado, Aspen Executive Limo is ready to provide luxury transportation tailored to your schedule.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/reserve" data-testid="rates-final-quote-btn" className={CTA_BTN}>Request a Quote <ArrowRight size={16} /></Link>
            <a href={`tel:${PHONE}`} data-testid="rates-final-call-btn" className={CTA_BTN}><Phone size={16} /> Call or Text Now</a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
